import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Plus, Trash2, X, Loader2, Image as ImageIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable Photos tile for a customer.
 * - GET    /api/customers/:id/photos
 * - POST   /api/customers/:id/photos    (multipart "file")
 * - DELETE /api/customers/:id/photos/:photoId
 *
 * Props:
 *  - customerId (required)
 *  - title (default "Photos")
 *  - readOnly (default false) — workers without permission may pass true
 *  - className (optional)
 */
const CustomerPhotos = ({ customerId, title = 'Photos', readOnly = false, className = '' }) => {
  const [photos, setPhotos] = useState([]);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null); // photoId being viewed full-screen
  const [photoUrls, setPhotoUrls] = useState({}); // photoId -> object URL
  const fileInputRef = useRef(null);

  const token = typeof window !== 'undefined'
    ? (localStorage.getItem('streakfree_token') || localStorage.getItem('token'))
    : null;

  const fetchOne = useCallback(async (photoId) => {
    try {
      const res = await fetch(`${API_URL}/api/customers/${customerId}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error('Photo fetch failed', err);
      return null;
    }
  }, [customerId, token]);

  const loadPhotos = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/customers/${customerId}/photos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load photos');
      const data = await res.json();
      setPhotos(data.photos || []);
      setLimit(data.limit || 20);

      // Fetch image binaries for thumbnails
      const urls = {};
      await Promise.all(
        (data.photos || []).map(async (p) => {
          const url = await fetchOne(p.id);
          if (url) urls[p.id] = url;
        })
      );
      setPhotoUrls((prev) => {
        // Revoke old URLs
        Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
        return urls;
      });
    } catch (err) {
      setError(err.message || 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [customerId, token, fetchOne]);

  useEffect(() => {
    loadPhotos();
    return () => {
      // Cleanup blob URLs on unmount
      setPhotoUrls((prev) => {
        Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
        return {};
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const handleFileSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setError('');
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          setError(`${file.name} is not an image — skipped`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_URL}/api/customers/${customerId}/photos`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          setError(errData.detail || `Upload failed for ${file.name}`);
          break;
        }
      }
      await loadPhotos();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (photoId, ev) => {
    ev?.stopPropagation();
    if (!window.confirm('Delete this photo?')) return;
    try {
      const res = await fetch(`${API_URL}/api/customers/${customerId}/photos/${photoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      // Revoke URL and remove
      if (photoUrls[photoId]) URL.revokeObjectURL(photoUrls[photoId]);
      setPhotoUrls((prev) => {
        const next = { ...prev };
        delete next[photoId];
        return next;
      });
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      if (lightbox === photoId) setLightbox(null);
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const triggerUpload = () => {
    if (readOnly || uploading) return;
    fileInputRef.current?.click();
  };

  return (
    <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm ${className}`} data-testid="customer-photos-tile">
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
          <Camera size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-zinc-900">{title}</p>
          <p className="text-xs text-zinc-500">
            {photos.length === 0
              ? 'Add access notes, gate codes, velux locations'
              : `${photos.length} of ${limit} photos`}
          </p>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={triggerUpload}
            disabled={uploading || photos.length >= limit}
            className="inline-flex items-center gap-1 px-3 py-2 bg-violet-500 hover:bg-violet-600 disabled:bg-violet-300 text-white text-sm font-medium rounded-xl transition-colors"
            data-testid="add-photo-btn"
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            <span>{uploading ? 'Uploading…' : 'Add'}</span>
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileSelected}
          data-testid="photo-file-input"
        />
      </div>

      {error && (
        <div className="mx-4 mb-3 p-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700" data-testid="photo-error">
          {error}
        </div>
      )}

      <div className="px-4 pb-4">
        {loading ? (
          <div className="py-6 flex items-center justify-center text-zinc-400 text-sm gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading photos…
          </div>
        ) : photos.length === 0 ? (
          <button
            type="button"
            onClick={triggerUpload}
            disabled={readOnly}
            className="w-full py-8 border-2 border-dashed border-zinc-200 rounded-xl flex flex-col items-center gap-2 text-zinc-400 hover:border-violet-300 hover:text-violet-500 transition-colors disabled:cursor-default disabled:hover:border-zinc-200 disabled:hover:text-zinc-400"
            data-testid="photos-empty-state"
          >
            <ImageIcon size={28} />
            <span className="text-xs font-medium">{readOnly ? 'No photos yet' : 'Tap to add a photo'}</span>
          </button>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {photos.map((p) => (
              <div
                key={p.id}
                className="relative aspect-square rounded-xl overflow-hidden bg-zinc-100 group cursor-pointer"
                onClick={() => setLightbox(p.id)}
                data-testid={`photo-${p.id}`}
              >
                {photoUrls[p.id] ? (
                  <img src={photoUrls[p.id]} alt={p.filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-300">
                    <Loader2 size={20} className="animate-spin" />
                  </div>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={(e) => handleDelete(p.id, e)}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-photo-${p.id}`}
                    aria-label="Delete photo"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && photoUrls[lightbox] && (
        <div
          className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="photo-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <img
            src={photoUrls[lightbox]}
            alt="Customer"
            className="max-w-full max-h-full rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          {!readOnly && (
            <button
              type="button"
              onClick={(e) => handleDelete(lightbox, e)}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium"
              data-testid="lightbox-delete-btn"
            >
              <Trash2 size={16} /> Delete photo
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerPhotos;
